
import { toast } from "sonner";
import { saveInitialBroCode } from '@/lib/broCode';
import { useAuthenticatedApi } from '@/hooks/useAuthenticatedApi';
import { supabase } from '@/integrations/supabase/client';

interface QuizCompletionProps {
  questions: any[];
  selectedOptions: string[];
  selectedOption: string | null;
  correctAnswers: number;
  currentQuestionIndex: number;
  walletAddress: `0x${string}`;
  hasToken: boolean;
  onComplete: (rank: string, brocode: string) => void;
  checkAnswer: () => boolean;
}

export const useQuizCompletion = ({
  questions,
  correctAnswers,
  walletAddress,
  hasToken,
  onComplete,
  checkAnswer
}: QuizCompletionProps) => {
  const { invokeFunction, isAuthenticated } = useAuthenticatedApi();
  
  // Function to get user's IP address
  const getUserIP = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error('Error getting IP:', error);
      return '0.0.0.0'; // Fallback IP
    }
  };

  const handleQuizCompletion = async () => {
    // Check if they got all answers correct (including the current one)
    const isCurrentAnswerCorrect = checkAnswer();
    const allCorrect = correctAnswers + (isCurrentAnswerCorrect ? 1 : 0) === questions.length;
    
    if (!allCorrect) {
      toast.error("The Swarm rejects the unprepared. Try again, bro.");
      return { success: false };
    }

    // Normalize wallet address to lowercase for consistency
    const normalizedAddress = walletAddress.toLowerCase();

    try {
      // Check if we're in authenticated context (Telegram or other)
      if (isAuthenticated) {
        // Authenticated flow - use complete-initiation edge function
        console.log('🎯 Processing cipher completion via authenticated flow');
        
        try {
          const data = await invokeFunction('complete-initiation', {
            walletAddress: normalizedAddress,
            chainType: 'sol', // Could be dynamic based on wallet type
            chosenPath: 'cipher',
            twitterHandle: null // Twitter handle would be linked separately
          });

          if (!data?.success) {
            if (data?.error?.includes('already has a BroCode')) {
              toast.success("Welcome back! You already have a BroCode.");
              onComplete(data.userData?.rank || "Shadow Bro", data.userData?.brocode || normalizedAddress.slice(0, 6) + '...' + normalizedAddress.slice(-4));
              return { success: true };
            }
            
            console.error('Complete-initiation failed:', data?.error);
            toast.error("There was an error saving your results, but your progress will continue.");
            onComplete(hasToken ? "Initiated Bro" : "Shadow Bro", normalizedAddress.slice(0, 6) + '...' + normalizedAddress.slice(-4));
            return { success: true };
          }

          console.log('✅ Authenticated cipher completion successful:', data.userData?.brocode);
          toast.success("You've been initiated into the brotherhood!");
          onComplete(data.userData?.rank || "Shadow Bro", data.userData?.brocode || normalizedAddress.slice(0, 6) + '...' + normalizedAddress.slice(-4));
          return { success: true };
        } catch (authError) {
          console.error('Error in authenticated cipher completion:', authError);
          toast.error("Authentication failed. Falling back to web flow.");
          // Fall through to web flow
        }
      }
      
      // Web-only flow - use legacy IP-based restrictions and broCode service
      console.log('🌐 Processing cipher completion via web flow');
      
      // Get user's IP address for web-only users
      const userIP = await getUserIP();
      
      // Check if IP is eligible for cipher (only for non-token holders in web flow)
      if (!hasToken) {
        try {
          const { data: isEligible, error } = await supabase.rpc('check_ip_cipher_eligibility', {
            ip_param: userIP,
            wallet_param: normalizedAddress,
            has_token: hasToken
          });
          
          if (error) {
            console.error('Error checking IP eligibility:', error);
          } else if (!isEligible) {
            toast.error("This IP address has already been used to complete the cipher. Only one attempt per IP for non-token holders.");
            return { success: false };
          }
        } catch (error) {
          console.error('Error checking IP eligibility:', error);
        }
      }

      // Determine their rank and score based on TOP1 token ownership
      const rank = hasToken ? "Initiated Bro" : "Shadow Bro";
      const broScore = hasToken ? 50 : 10;
      
      console.log(`Saving cipher result for wallet: ${normalizedAddress}, rank: ${rank}, score: ${broScore}`);
      
      // Generate and save BroCode to initiated_bros table
      const brocode = await saveInitialBroCode(normalizedAddress, rank, broScore);
      
      // Record cipher attempt with IP tracking (for non-token holders)
      if (!hasToken) {
        try {
          await supabase.rpc('record_cipher_attempt', {
            ip_param: userIP,
            wallet_param: normalizedAddress,
            completed: true
          });
          console.log(`Recorded cipher attempt for Shadow Bro: ${normalizedAddress}, IP: ${userIP}`);
        } catch (error) {
          console.error('Error recording cipher attempt:', error);
        }
      }
      
      if (!brocode) {
        console.error("Error generating BroCode");
        toast.error("There was an error saving your results, but your progress will continue.");
        onComplete(rank, normalizedAddress.slice(0, 6) + '...' + normalizedAddress.slice(-4));
      } else {
        console.log("BroCode generated successfully:", brocode);
        toast.success("You've been initiated into the brotherhood!");
        onComplete(rank, brocode);
      }
      
      return { success: true };
    } catch (error) {
      console.error("Unexpected error in quiz completion:", error);
      toast.error("There was an error saving your results, but your progress will continue.");
      
      // Still proceed to completion with a fallback
      const rank = hasToken ? "Initiated Bro" : "Shadow Bro";
      onComplete(rank, walletAddress.slice(0, 6) + '...' + walletAddress.slice(-4));
      return { success: true };
    }
  };

  return { handleQuizCompletion };
};
